import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Collapse, Form, Modal, Row, Select, Typography } from 'antd';
import _ from 'lodash';
import { validateMessages } from '../../../../common/constants/common';
import { IInlineSearch } from '../../../../common/models/common';
import { ILookup } from '../../../../services/common/common.model';
import azureOnboardingService from '../../../../services/azure/azureOnboarding/azureOnboarding.service';
import {
  IAzureOnboardingCreated,
  ICreateAzureOnboarding,
} from '../../../../services/azure/azureOnboarding/azureOnboarding.model';
import { useAppDispatch, useAppSelector } from '../../../../store/app.hooks';
import {
  getBULookup,
  getCompanyLookup,
  getTenantLookup,
} from '../../../../store/common/common.action';
import {
  clearBULookUp,
  clearCompanyLookUp,
  commonSelector,
} from '../../../../store/common/common.reducer';
import { globalSearchSelector } from '../../../../store/globalSearch/globalSearch.reducer';
import { clientLink } from '../azureOnboarding.utils';
import CopyField from './CopyField';

const { Option } = Select;
const { Text } = Typography;

interface NewOnboardingModalProps {
  existingIds: number[];
  onClose: () => void;
  onCreated: () => void;
}

const filterOption = (input: string, option: any) =>
  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
const filterSort = (optionA: any, optionB: any) =>
  optionA.children?.toLowerCase()?.localeCompare(optionB.children?.toLowerCase());

const renderOptions = (options?: ILookup[]) =>
  (options || []).map((option: ILookup) => (
    <Option key={option.id} value={option.id}>
      {option.name}
    </Option>
  ));

const NewOnboardingModal: React.FC<NewOnboardingModalProps> = ({
  existingIds,
  onClose,
  onCreated,
}) => {
  const commonLookups = useAppSelector(commonSelector);
  const globalFilters = useAppSelector(globalSearchSelector);
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();

  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState<IAzureOnboardingCreated | null>(null);
  const [alreadyExisted, setAlreadyExisted] = useState(false);

  const onFinish = async (values: ICreateAzureOnboarding) => {
    setSaving(true);
    try {
      const result = await azureOnboardingService.create({
        tenant_id: values.tenant_id,
        company_id: values.company_id,
        bu_id: values.bu_id,
      });
      setAlreadyExisted(existingIds.includes(result.oauth_id));
      setCreated(result);
      onCreated();
    } catch {
      /* handled by request interceptor */
    } finally {
      setSaving(false);
    }
  };

  const handleTenantChange = (tenantId: number) => {
    form.setFieldsValue({ tenant_id: tenantId, company_id: null, bu_id: null });
    if (tenantId) {
      dispatch(getCompanyLookup(tenantId));
      dispatch(clearBULookUp());
    } else {
      dispatch(clearCompanyLookUp());
      dispatch(clearBULookUp());
    }
  };

  const handleCompanyChange = (companyId: number) => {
    form.setFieldsValue({ company_id: companyId, bu_id: null });
    if (companyId) {
      dispatch(getBULookup(companyId));
    } else {
      dispatch(clearBULookUp());
    }
  };

  useEffect(() => {
    if (Object.keys(globalFilters?.globalTenantLookup?.data).length == 0) {
      dispatch(getTenantLookup());
    }

    const globalSearch: IInlineSearch = {};
    for (const key in globalFilters.search) {
      const element = globalFilters.search[key];
      globalSearch[key] = element ? [element] : null;
    }
    if (globalFilters.search.tenant_id && globalFilters.search.tenant_id !== 0) {
      if (!globalFilters.search.company_id) {
        dispatch(getCompanyLookup(globalSearch.tenant_id[0]));
      }
      if (!globalFilters.search.bu_id && globalFilters.search.company_id !== 0) {
        dispatch(getBULookup(globalSearch.company_id[0]));
      }
      form.setFieldsValue({
        company_id: _.isNull(globalSearch.company_id) ? null : globalSearch.company_id[0],
        bu_id: _.isNull(globalSearch.bu_id) ? null : globalSearch.bu_id[0],
        tenant_id: _.isNull(globalSearch.tenant_id) ? null : globalSearch.tenant_id[0],
      });
    }

    return () => {
      dispatch(clearCompanyLookUp());
      dispatch(clearBULookUp());
    };
  }, []);

  return (
    <Modal
      wrapClassName="custom-modal"
      title="New Azure Onboarding"
      centered
      visible
      onCancel={onClose}
      footer={null}
      maskClosable={!created}
      width={720}
    >
      {created ? (
        <>
          {alreadyExisted && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="This BU already has an onboarding. Here is its link."
            />
          )}
          <div className="form-group">
            <Text strong>Client link</Text>
            <CopyField label="Client link" value={clientLink(created.link_token)} primary />
            <Text type="secondary">
              Send this link to the client&apos;s Azure Global Administrator. It doesn&apos;t
              expire.
            </Text>
          </div>
          <Collapse ghost>
            <Collapse.Panel header="Consent URL" key="consent-url">
              <CopyField label="Consent URL" value={created.consent_url} />
              <Text type="secondary">
                Direct Microsoft consent link, valid for 24 hours. Prefer the client link.
              </Text>
            </Collapse.Panel>
          </Collapse>
          <div className="btns-block modal-footer">
            <Button type="primary" onClick={onClose}>
              Done
            </Button>
          </div>
        </>
      ) : (
        <Form
          form={form}
          name="azureOnboarding"
          initialValues={{ tenant_id: null, company_id: null, bu_id: null }}
          onFinish={onFinish}
          validateMessages={validateMessages}
        >
          <Row gutter={[30, 15]} className="form-label-hide">
            <Col xs={24} sm={12} md={8}>
              <div className="form-group m-0">
                Tenant
                <Form.Item
                  name="tenant_id"
                  className="m-0"
                  label="Tenant"
                  rules={[{ required: true }]}
                >
                  <Select
                    onChange={handleTenantChange}
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    filterOption={filterOption}
                    filterSort={filterSort}
                    loading={commonLookups.tenantLookup.loading}
                  >
                    {Object.keys(globalFilters?.globalTenantLookup?.data).length > 0
                      ? renderOptions(globalFilters?.globalTenantLookup?.data)
                      : renderOptions(commonLookups.tenantLookup.data)}
                  </Select>
                </Form.Item>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div className="form-group m-0">
                Company
                <Form.Item
                  name="company_id"
                  className="m-0"
                  label="Company"
                  rules={[{ required: true }]}
                >
                  <Select
                    onChange={handleCompanyChange}
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    filterOption={filterOption}
                    filterSort={filterSort}
                    loading={commonLookups.companyLookup.loading}
                  >
                    {Object.keys(commonLookups.companyLookup.data).length > 0
                      ? renderOptions(commonLookups.companyLookup.data)
                      : renderOptions(globalFilters?.globalCompanyLookup?.data)}
                  </Select>
                </Form.Item>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div className="form-group m-0">
                BU
                <Form.Item name="bu_id" className="m-0" label="BU" rules={[{ required: true }]}>
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    filterOption={filterOption}
                    filterSort={filterSort}
                    loading={commonLookups.buLookup.loading}
                  >
                    {Object.keys(commonLookups.buLookup.data).length > 0
                      ? renderOptions(commonLookups.buLookup.data)
                      : renderOptions(globalFilters?.globalBULookup?.data)}
                  </Select>
                </Form.Item>
              </div>
            </Col>
          </Row>
          <div className="btns-block modal-footer">
            <Button type="primary" htmlType="submit" loading={saving}>
              Create onboarding
            </Button>
            <Button onClick={onClose}>Cancel</Button>
          </div>
        </Form>
      )}
    </Modal>
  );
};

export default NewOnboardingModal;
